/**
 * localStorage adapter for pinned products (PSS-2655).
 *
 * This module deliberately contains no React and no DOM access beyond
 * localStorage reads/writes. It is the seam a future server-backed
 * implementation replaces: swap out fetchPinnedProducts and
 * savePinnedProducts for real fetch/save calls and every UI call site
 * stays unchanged.
 */

const storageKey = (entityId: number, userId: string) =>
  `psp-pinned-products-${userId}-${entityId}`;

/**
 * Safely deserializes the stored pinned-products string. Falls back to `[]`
 * on missing or corrupt data, and filters out non-string entries so one bad
 * record cannot discard the rest of a user's list.
 */
export function parsePinnedProducts(raw: string | null | undefined): string[] {
  if (raw == null) return [];
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return [];
  }
  if (!Array.isArray(parsed)) return [];
  return parsed.filter((item): item is string => typeof item === 'string');
}

// ─── Replace these two functions with real fetch/save when a backend
//     endpoint is available. The hook and all UI call sites are unchanged. ───

export async function fetchPinnedProducts(entityId: number, userId: string): Promise<string[]> {
  return parsePinnedProducts(localStorage.getItem(storageKey(entityId, userId)));
}

export async function savePinnedProducts(
  entityId: number,
  userId: string,
  basePaths: string[]
): Promise<void> {
  localStorage.setItem(storageKey(entityId, userId), JSON.stringify(basePaths));
}
